import { PLAYER_AIM_PITCH_MIN, PLAYER_AIM_PITCH_MAX } from "./player-control.js";
import { isMedicalDeveloperCommand } from "./player-condition.js";
import { isClientCombatDeveloperCommand } from "./combat.js";
import { PHYSICAL_ACTIONS } from "./items/physical.js";
import { EQUIPMENT_SLOTS } from "./items/types.js";
import { ITEM_CATALOG_HASH, ITEM_CATALOG_VERSION } from "./items/catalog.js";
import { isDroneId, isDroneShoutText } from "./drone.js";
import { isClientNpcMessage } from "./live-npc.js";
export const PROTOCOL_VERSION = 14;
export const REMOTE_PLAYER_VISIBILITY_RADIUS = 80;
export const PLAYER_INTEREST_RADIUS = 128;
export const DEVELOPER_SPEEDS = Object.freeze([1, 2, 4, 10, 25, 50]);
export const DEVELOPER_TELEPORT_TARGET_IDS = Object.freeze([
    "START",
    "INTERACTION_TEST",
    "ASHLANDS_CORE",
    "GROUND_ZERO_CORE",
    "FERTILE_CORE",
    "AI_FARM",
    "NUCLEAR_FOREST_CORE",
    "RUINED_CIV_CORE",
    "INDUSTRIAL_CORE",
    "HIGHWAY_GAS_STATION",
    "WETWARE_DATACENTER",
    "ASH_TO_FOREST_TRANSITION",
    "FERTILE_TO_ASH_TRANSITION",
    "GROUND_ZERO_BOUNDARY",
]);
export const DEFAULT_DEVELOPER_STATE = Object.freeze({
    authorized: false,
    noclip: false,
    freeFly: false,
    speed: 1,
});
/** No private inventory, battery, or bone matrices enter the bounded pose tuple. */
export function isPackedPlayer(value) {
    if (!Array.isArray(value) || value.length !== 22 || typeof value[0] !== "string" || !value[0].length)
        return false;
    if (!value.slice(1).every((field) => typeof field === "number" && Number.isFinite(field)))
        return false;
    return [5, 16, 17, 21].every((i) => Number.isSafeInteger(value[i]) && value[i] >= 0)
        && Number.isInteger(value[6]) && value[6] >= 0 && value[6] <= 3
        && value[7] >= 0 && value[7] <= 1
        && Math.abs(value[4]) <= Math.PI + 0.00001 && Math.abs(value[11]) <= Math.PI + 0.00001
        && value[12] >= PLAYER_AIM_PITCH_MIN && value[12] <= PLAYER_AIM_PITCH_MAX
        && (value[13] === 0 || value[13] === 1) && [0, 1, 2].includes(value[14])
        && [0, 1, 2].includes(value[15]) && (value[20] === 0 || value[20] === 1);
}
export const INVENTORY_RESULT_CODES = Object.freeze([
    "OK",
    "NO_ROOM",
    "BUSY",
    "INVALID_INVENTORY",
    "INVALID_REQUEST",
    "INVALID_SLOT",
    "INVALID_STACK",
    "INVALID_QUANTITY",
    "STACK_CHANGED",
    "NO_FREE_SLOT",
    "INVENTORY_FULL",
    "TOO_HEAVY",
    "CONTAINER_TOO_FAR",
    "CONTAINER_CLOSED",
    "STALE_STATE",
    "ITEM_GONE",
    "NOT_AUTHORIZED",
    "RATE_LIMITED",
    "DROP_LIMIT_REACHED",
    "INVALID_TARGET",
]);
export const DEFAULT_APPEARANCE = {
    name: "Wanderer",
    body: "female",
    skin: 1,
    hair: 2,
    hairColor: 0,
    face: 0,
    top: 1,
    trousers: 0,
    shoes: 0,
};
export function isClientMessage(value) {
    if (!value || typeof value !== "object")
        return false;
    const candidate = value;
    if (isClientNpcMessage(value))
        return true;
    if (candidate.t === "activity")
        return hasExactKeys(candidate, ["t", "kind"])
            && (candidate.kind === "camera" || candidate.kind === "interaction" || candidate.kind === "ui");
    if (candidate.t === "drone_shout") {
        return hasExactKeys(candidate, ["t", "droneId", "requestId", "text"])
            && isDroneId(candidate.droneId) && isRequestIdentifier(candidate.requestId) && isDroneShoutText(candidate.text);
    }
    if (candidate.t === "drone_subscribe") {
        return hasExactKeys(candidate, ["t", "droneId"]) && (candidate.droneId === null || isDroneId(candidate.droneId));
    }
    if (candidate.t === "ping")
        return typeof candidate.at === "number" && Number.isFinite(candidate.at);
    if (candidate.t === "i")
        return isPlayerCommand(candidate);
    if (candidate.t === "interaction_start") {
        return (hasExactKeys(candidate, ["t", "requestId", "target", "actionId"]) &&
            isBoundedIdentifier(candidate.requestId, 64, /^[A-Za-z0-9_-]+$/) &&
            isInteractionTargetRef(candidate.target) &&
            isBoundedIdentifier(candidate.actionId, 32, /^[a-z0-9_-]+$/));
    }
    if (candidate.t === "interaction_cancel") {
        return (hasExactKeys(candidate, ["t", "requestId"]) &&
            isBoundedIdentifier(candidate.requestId, 64, /^[A-Za-z0-9_-]+$/));
    }
    if (candidate.t === "dev_auth") {
        return typeof candidate.password === "string" && candidate.password.length >= 1 && candidate.password.length <= 128;
    }
    if (candidate.t === "dev_cmd") {
        if (isMedicalDeveloperCommand(candidate) || isClientCombatDeveloperCommand(candidate))
            return true;
        if (candidate.command === "battle_player_damage")
            return hasExactKeys(candidate, ["t", "command", "enabled"]) && typeof candidate.enabled === "boolean";
        if (candidate.command === "noclip")
            return typeof candidate.enabled === "boolean";
        if (candidate.command === "fly")
            return typeof candidate.enabled === "boolean";
        if (candidate.command === "speed")
            return isDeveloperSpeed(candidate.speed);
        if (candidate.command === "teleport") {
            if (candidate.target !== undefined)
                return isDeveloperTeleportTarget(candidate.target);
            return (typeof candidate.x === "number" && Number.isFinite(candidate.x) &&
                typeof candidate.z === "number" && Number.isFinite(candidate.z) &&
                (candidate.y === undefined || (typeof candidate.y === "number" && Number.isFinite(candidate.y))));
        }
        if (candidate.command === "reset")
            return true;
        if (candidate.command === "give_item") {
            return (hasExactKeys(candidate, ["t", "command", "definitionId", "quantity"]) &&
                isDefinitionIdentifier(candidate.definitionId) &&
                isPositiveSafeInteger(candidate.quantity));
        }
        if (candidate.command === "spawn_world_item") {
            return (hasExactKeys(candidate, ["t", "command", "definitionId", "quantity"]) &&
                isDefinitionIdentifier(candidate.definitionId) &&
                isPositiveSafeInteger(candidate.quantity));
        }
        if (candidate.command === "clear_inventory" || candidate.command === "reset_test_cache") {
            return hasExactKeys(candidate, ["t", "command"]);
        }
        return false;
    }
    if (candidate.t === "inventory_resync")
        return hasExactKeys(candidate, ["t"]);
    if (candidate.t === "inventory_move") {
        return hasExactKeys(candidate, ["t", "requestId", "expectedRevision", "sourceStackId", "destinationSlot"])
            && isRequestIdentifier(candidate.requestId)
            && isRevision(candidate.expectedRevision)
            && isStackIdentifier(candidate.sourceStackId)
            && isSlot(candidate.destinationSlot);
    }
    if (candidate.t === "inventory_split") {
        return hasExactKeys(candidate, ["t", "requestId", "expectedRevision", "sourceStackId", "quantity", "destinationSlot"])
            && isRequestIdentifier(candidate.requestId)
            && isRevision(candidate.expectedRevision)
            && isStackIdentifier(candidate.sourceStackId)
            && isPositiveSafeInteger(candidate.quantity)
            && isSlot(candidate.destinationSlot);
    }
    if (candidate.t === "inventory_drop") {
        return hasExactKeys(candidate, ["t", "requestId", "expectedRevision", "sourceStackId", "quantity"])
            && isRequestIdentifier(candidate.requestId)
            && isRevision(candidate.expectedRevision)
            && isStackIdentifier(candidate.sourceStackId)
            && isPositiveSafeInteger(candidate.quantity);
    }
    if (candidate.t === "container_transfer") {
        return hasExactKeys(candidate, [
            "t",
            "requestId",
            "accessSessionId",
            "direction",
            "sourceStackId",
            "quantity",
            "expectedInventoryRevision",
            "expectedContainerRevision",
            ...(candidate.destinationPocket === undefined ? [] : ["destinationPocket"]),
        ])
            && isRequestIdentifier(candidate.requestId)
            && isBoundedIdentifier(candidate.accessSessionId, 80, /^[A-Za-z0-9_-]+$/)
            && (candidate.direction === "CONTAINER_TO_PLAYER" || candidate.direction === "PLAYER_TO_CONTAINER")
            && isStackIdentifier(candidate.sourceStackId)
            && isPositiveSafeInteger(candidate.quantity)
            && isRevision(candidate.expectedInventoryRevision)
            && isRevision(candidate.expectedContainerRevision)
            && (candidate.destinationPocket === undefined || isPocketRef(candidate.destinationPocket));
    }
    if (candidate.t === "container_close") {
        return hasExactKeys(candidate, ["t", "accessSessionId"])
            && isBoundedIdentifier(candidate.accessSessionId, 80, /^[A-Za-z0-9_-]+$/);
    }
    if (candidate.t === "container_resync") {
        return hasExactKeys(candidate, ["t", "accessSessionId"])
            && isBoundedIdentifier(candidate.accessSessionId, 80, /^[A-Za-z0-9_-]+$/);
    }
    if (candidate.t === "physical_action") {
        const allowed = ["t", "requestId", "expectedRevision", "action", "itemInstanceId", "pocket", "destination", "cellSlot", "batteryInstanceId", "equipmentSlot", "closureId", "enabled"];
        const ref = (value) => {
            if (!value || typeof value !== 'object')
                return false;
            const pocket = value;
            return hasExactKeys(pocket, ["garmentInstanceId", "pocketId"]) && isStackIdentifier(pocket.garmentInstanceId)
                && isBoundedIdentifier(pocket.pocketId, 40, /^[A-Z0-9_]+$/);
        };
        return Object.keys(candidate).every(key => allowed.includes(key))
            && isRequestIdentifier(candidate.requestId) && isRevision(candidate.expectedRevision)
            && PHYSICAL_ACTIONS.some(action => action === candidate.action)
            && (candidate.itemInstanceId === undefined || isStackIdentifier(candidate.itemInstanceId))
            && (candidate.batteryInstanceId === undefined || isStackIdentifier(candidate.batteryInstanceId))
            && (candidate.pocket === undefined || ref(candidate.pocket))
            && (candidate.destination === undefined || ref(candidate.destination))
            && (candidate.cellSlot === undefined || (Number.isSafeInteger(candidate.cellSlot) && candidate.cellSlot >= 0 && candidate.cellSlot < 8))
            && (candidate.equipmentSlot === undefined || EQUIPMENT_SLOTS.some(slot => slot === candidate.equipmentSlot))
            && (candidate.enabled === undefined || typeof candidate.enabled === 'boolean')
            && (candidate.closureId === undefined || isBoundedIdentifier(candidate.closureId, 40, /^[A-Za-z0-9_]+$/));
    }
    if (candidate.t === "hello") {
        return (hasExactKeys(candidate, ["t", "v", "itemCatalogVersion", "itemCatalogHash", "appearance"]) &&
            candidate.v === PROTOCOL_VERSION &&
            candidate.itemCatalogVersion === ITEM_CATALOG_VERSION &&
            candidate.itemCatalogHash === ITEM_CATALOG_HASH &&
            isAppearance(candidate.appearance));
    }
    return false;
}
function hasExactKeys(candidate, expected) {
    const keys = Object.keys(candidate);
    return keys.length === expected.length && keys.every((key) => expected.includes(key));
}
function isBoundedIdentifier(value, maximumLength, pattern) {
    return (typeof value === "string" &&
        value.length >= 1 &&
        value.length <= maximumLength &&
        pattern.test(value));
}
function isRequestIdentifier(value) {
    return isBoundedIdentifier(value, 64, /^[A-Za-z0-9_-]+$/);
}
function isStackIdentifier(value) {
    return isBoundedIdentifier(value, 80, /^[A-Za-z0-9_-]+$/);
}
function isDefinitionIdentifier(value) {
    return isBoundedIdentifier(value, 96, /^[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+$/);
}
function isPositiveSafeInteger(value) {
    return Number.isSafeInteger(value) && value > 0;
}
function isRevision(value) {
    return Number.isSafeInteger(value) && value >= 0;
}
function isSlot(value) {
    return Number.isSafeInteger(value) && value >= 0 && value <= 1_023;
}
function isInteractionTargetRef(value) {
    if (!value || typeof value !== "object")
        return false;
    const target = value;
    return hasExactKeys(target, ["kind", "id"])
        && (target.kind === "WORLD_OBJECT" || target.kind === "DROPPED_ITEM")
        && isBoundedIdentifier(target.id, 192, /^[A-Za-z0-9:_-]+$/);
}
function isDeveloperSpeed(value) {
    return typeof value === "number" && DEVELOPER_SPEEDS.some((speed) => speed === value);
}
function isDeveloperTeleportTarget(value) {
    return typeof value === "string" && DEVELOPER_TELEPORT_TARGET_IDS.some((target) => target === value);
}
function isAppearance(value) {
    if (!value || typeof value !== "object")
        return false;
    const a = value;
    return (typeof a.name === "string" &&
        a.name.length >= 1 &&
        a.name.length <= 20 &&
        (a.body === "male" || a.body === "female") &&
        [a.skin, a.hair, a.hairColor, a.face, a.top, a.trousers, a.shoes].every((entry) => typeof entry === "number" && Number.isInteger(entry) && entry >= 0 && entry <= 15));
}
function isPocketRef(value) {
    if (!value || typeof value !== 'object')
        return false;
    const ref = value;
    return hasExactKeys(ref, ['garmentInstanceId', 'pocketId']) && isStackIdentifier(ref.garmentInstanceId) && isBoundedIdentifier(ref.pocketId, 40, /^[A-Z0-9_]+$/);
}
/** Public transport rejects old command layouts even though the hello is versioned. */
export function isPlayerCommand(candidate) {
    if (!candidate || typeof candidate !== "object")
        return false;
    const c = candidate;
    const keys = ["t", "s", "e", "x", "z", "run", "aimYaw", "aimPitch", "stance", "gait", "actions"];
    if (c.vertical !== undefined)
        keys.push("vertical");
    return hasExactKeys(c, keys) && c.t === "i" && Number.isSafeInteger(c.s) && c.s > 0
        && Number.isSafeInteger(c.e) && c.e >= 0
        && typeof c.x === "number" && Number.isFinite(c.x) && Math.abs(c.x) <= 1
        && typeof c.z === "number" && Number.isFinite(c.z) && Math.abs(c.z) <= 1
        && Math.hypot(c.x, c.z) <= 1.001
        && typeof c.aimYaw === "number" && Number.isFinite(c.aimYaw) && Math.abs(c.aimYaw) <= Math.PI
        && typeof c.aimPitch === "number" && Number.isFinite(c.aimPitch)
        && c.aimPitch >= PLAYER_AIM_PITCH_MIN && c.aimPitch <= PLAYER_AIM_PITCH_MAX
        && (c.stance === 0 || c.stance === 1) && (c.gait === 0 || c.gait === 1 || c.gait === 2)
        && Number.isInteger(c.actions) && c.actions >= 0 && c.actions <= 15
        && (c.run === 0 || c.run === 1)
        && (c.vertical === undefined || c.vertical === -1 || c.vertical === 0 || c.vertical === 1);
}
